import DisplayContainer from "./DisplayContainer.ts";
import ILandingAnimation from "../interfaces/ILandingAnimation.ts";
import { Sprite, Texture, TextureSource } from "pixi.js";
import Convert from "../utils/Convert.ts";
import { ColorOverlayFilter, DropShadowFilter, OutlineFilter } from "pixi-filters";
import TimelineManager from "@/managers/TimelineManager.ts";

export default class ImageButton extends DisplayContainer implements ILandingAnimation {
    private DISABLED_ALPHA = 0.5;

    private _animationPlaying: boolean = false;
    private _centerRegistered: boolean;
    private _hasFocus: boolean;
    private _imageStrokeColor: string;
    private _outlined: boolean;
    private outlineFilter: OutlineFilter;
    public readonly imageId: Texture | TextureSource | string;

    protected image: Sprite;
    protected imageStroke: Sprite;

    public get animationPlaying(): boolean {
        return this._animationPlaying;
    }

    public set centerRegistered(value: boolean) {
        this._centerRegistered = value;
    }

    public get hasFocus(): boolean {
        return this._hasFocus;
    }

    public set hasFocus(value: boolean) {
        this._hasFocus = value;
        if (this._hasFocus) {
            this.focus();
        } else {
            this.unfocus();
        }
    }

    public set imageStrokeColor(value: string) {
        this._imageStrokeColor = value;
    }

    public get outlined(): boolean {
        return this._outlined;
    }

    public set outlined(value: boolean) {
        this._outlined = value;
        if (value) {
            this.filters = [this.outlineFilter];
        } else {
            this.filters = [];
        }
    }

    constructor(imageId: Texture | TextureSource | string) {
        super();
        this.imageId = imageId;
        this._init();
    }

    protected _init(): void {
        this._centerRegistered = false;
        this._imageStrokeColor = "#00E2FF";
    }

    private addGlow(): void {
        if (this.image) {
            this.image.filters = [new DropShadowFilter({ resolution: 2 })];
        }
    }

    private addListeners(): void {
        this.onmouseout = this.handleRollout;
        this.onmouseover = this.handleRollover;
    }

    private createImage(): void {
        this.image = Sprite.from(this.imageId);
        if (this._centerRegistered) {
            this.image.pivot.set(this.image.width / 2, this.image.height / 2);
        }
        this.addChild(this.image);
    }

    private createImageStroke(): void {
        this.imageStroke = Sprite.from(this.imageId);
        const strokeScale = 1.15;
        const originalWidth = this.imageStroke.width;
        const originalHeight = this.imageStroke.height;
        const newWidth = this.imageStroke.width * strokeScale;
        const newHeight = this.imageStroke.height * strokeScale;

        if (this._centerRegistered) {
            this.imageStroke.pivot.set(this.imageStroke.width / 2, this.imageStroke.height / 2);
        }
        this.imageStroke.scale.x = this.imageStroke.scale.y = strokeScale;
        this.imageStroke.filters = [new ColorOverlayFilter({ color: Convert.hexToNumber(this._imageStrokeColor) })];
        if (!this._centerRegistered) {
            this.imageStroke.x -= (newWidth - originalWidth) / 2;
            this.imageStroke.y -= (newHeight - originalHeight) / 2;
        }
        this.imageStroke.visible = false;
        this.addChild(this.imageStroke);
    }

    private createOutlineFilter(): void {
        this.outlineFilter = new OutlineFilter({ thickness: 8, color: 0x26c7dc });
    }

    private handleRollout = (): void => {};

    private handleRollover = (): void => {};

    private removeGlow(): void {
        if (this.image) {
            this.image.filters = [];
        }
    }

    private removeListeners(): void {
        this.onmouseout = null;
        this.onmouseover = null;
    }

    public create(creationCompleteCallback?: () => void, show = true): void {
        if (this._imageStrokeColor) {
            this.createImageStroke();
        }
        this.createImage();
        this.createOutlineFilter();
        this.cursor = "pointer";
        this.eventMode = "static";
        this.addListeners();
        if (show) {
            this.visible = true;
        }
    }

    public destroy(): void {
        this.removeChildren();
        this.removeListeners();
        super.destroy();
    }

    public override disable(): void {
        this.removeListeners();
        this.alpha = this.DISABLED_ALPHA;
        super.disable();
    }

    public override enable(): void {
        this.addListeners();
        this.alpha = 1;
        super.enable();
    }

    public focus(): void {
        this._hasFocus = true;
        if (this.imageStroke) {
            this.imageStroke.visible = true;
        }
        //this.addGlow();
    }

    public onEnterAnimation(): void {
        this.cursor = null;
        this._animationPlaying = true;
        TimelineManager.to(this, {
            pixi: { alpha: 1, scale: 1 },
            duration: 1,
            ease: "power2.out",
            onComplete: () => {
                this._animationPlaying = false;
                this.cursor = "pointer";
            },
        });
    }

    public onExitAnimation(): void {}

    public unfocus(): void {
        this._hasFocus = false;
        if (this.imageStroke) {
            this.imageStroke.visible = false;
        }
        //this.removeGlow();
    }
}
